'use client'

import { useParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

const getCurrentHash = () =>
  typeof window !== 'undefined' ? window.location.hash.replace(/^#!?/, '') : ''

export const useURLHashState = () => {
  const params = useParams()
  const [hash, _setHash] = useState<string>(getCurrentHash())

  const setHash = useCallback(function setHash(newHash: string) {
    _setHash(newHash)

    if (newHash) {
      history.pushState({ useURLHashState: true }, '', `#${newHash}`)
    } // if previous hash was set using this hook, go back
    else if (history.state?.useURLHashState) {
      history.back()
    } // if previous hash was set using native browser, replace the state
    else {
      history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  // listen to next.js route changes
  useEffect(() => {
    const currentHash = getCurrentHash()
    _setHash(currentHash)
  }, [params])

  // listen to native browser route changes
  useEffect(() => {
    const handleHashChange = () => {
      const currentHash = getCurrentHash()
      _setHash(currentHash)
    }

    // update hash when using Next <Link> component
    const { pushState, replaceState } = window.history
    window.history.pushState = function (...args) {
      pushState.apply(window.history, args)
      setTimeout(() => {
        return _setHash(getCurrentHash())
      })
    }
    window.history.replaceState = function (...args) {
      replaceState.apply(window.history, args)
      setTimeout(() => {
        return _setHash(getCurrentHash())
      })
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])

  return [hash, setHash] as const
}
